import { Children, isValidElement, memo, type ReactNode, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function reactNodeToText(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(reactNodeToText).join('');
    if (isValidElement<{ children?: ReactNode }>(node)) return reactNodeToText(node.props.children);
    return Children.toArray(node).map(reactNodeToText).join('');
}

function normalizeMarkdown(raw: string): string {
    return (raw ?? '').replace(/\r\n/g, '\n');
}

function splitMarkdownIntoSections(raw: string): { head: string; details: string; tail: string } | null {
    const markdown = normalizeMarkdown(raw);
    const lines = markdown.split('\n');

    const findHeading = (startIndex: number, headingNames: string[]) => {
        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i] ?? '';
            const match = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
            if (!match) continue;
            const title = (match[2] ?? '').trim().toLowerCase();
            if (headingNames.includes(title)) return i;
        }

        return null;
    };

    const detailsIndex = findHeading(0, ['details']);
    if (detailsIndex === null) return null;

    const nextStepsIndex = findHeading(detailsIndex + 1, ['next steps', 'next step']);
    if (nextStepsIndex === null) return null;
    if (nextStepsIndex <= detailsIndex) return null;

    const head = lines.slice(0, detailsIndex).join('\n').trim();

    const details = lines
        .slice(detailsIndex + 1, nextStepsIndex)
        .join('\n')
        .trim();

    const tail = lines.slice(nextStepsIndex).join('\n').trim();

    if (!head || !tail) return null;
    if (details.length < 180) return null;

    return { head, details, tail };
}

function sanitizeHref(href: string | undefined): string | null {
    const raw = (href ?? '').trim();
    if (!raw) return null;

    const lower = raw.toLowerCase();
    if (lower.startsWith('javascript:')) return null;
    if (lower.startsWith('data:')) return null;
    if (lower.startsWith('vbscript:')) return null;

    return raw;
}

function MarkdownRenderer({ markdown }: { markdown: string }) {
    const content = useMemo(() => normalizeMarkdown(markdown), [markdown]);

    return (
        <div className="leading-relaxed text-zinc-100">
            <ReactMarkdown
                skipHtml
                components={{
                    h1: ({ children }) => <h1 className="mt-4 first:mt-0 mb-2 text-base font-semibold text-white">{children}</h1>,
                    h2: ({ children }) => <h2 className="mt-4 first:mt-0 mb-2 text-sm font-semibold text-white">{children}</h2>,
                    h3: ({ children }) => <h3 className="mt-3 first:mt-0 mb-1.5 text-sm font-semibold text-zinc-100">{children}</h3>,
                    h4: ({ children }) => <h4 className="mt-3 first:mt-0 mb-1.5 text-sm font-medium text-zinc-100">{children}</h4>,
                    p: ({ children }) => <p className="my-2 text-sm text-zinc-100">{children}</p>,
                    ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1 text-sm">{children}</ul>,
                    ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1 text-sm">{children}</ol>,
                    li: ({ children }) => <li className="text-sm text-zinc-100">{children}</li>,
                    blockquote: ({ children }) => (
                        <blockquote className="my-2 border-l-2 border-zinc-700 pl-3 text-sm text-zinc-200">{children}</blockquote>
                    ),
                    a: ({ children, href }) => {
                        const safeHref = sanitizeHref(href);
                        if (!safeHref) return <span className="text-blue-300">{children}</span>;
                        const isExternal = /^https?:\/\//i.test(safeHref);
                        return (
                            <a
                                className="text-blue-300 underline underline-offset-2 hover:text-blue-200"
                                href={safeHref}
                                rel={isExternal ? 'noreferrer noopener' : undefined}
                                target={isExternal ? '_blank' : undefined}
                            >
                                {children}
                            </a>
                        );
                    },
                    code: ({ className, children }) => {
                        const text = reactNodeToText(children);
                        const isBlock = Boolean(className);

                        if (isBlock) {
                            return <code className="font-mono text-[12px] text-zinc-100">{text}</code>;
                        }

                        return (
                            <code className="rounded border border-zinc-700/70 bg-zinc-950/60 px-1 py-0.5 font-mono text-[12px] text-zinc-100">
                                {text}
                            </code>
                        );
                    },
                    pre: ({ children }) => (
                        <pre className="my-2 overflow-x-auto rounded border border-zinc-700 bg-zinc-950/60 p-2 text-[12px]">{children}</pre>
                    ),
                    hr: () => <hr className="my-3 border-zinc-700" />,
                    input: (props) => <input {...props} disabled className="mr-2 translate-y-[1px] accent-blue-500" type="checkbox" />,
                    table: ({ children }) => (
                        <div className="my-2 overflow-x-auto">
                            <table className="min-w-full border-collapse text-left text-[12px] [&_th]:bg-zinc-900 [&_th]:text-zinc-200 [&_th]:font-semibold [&_th]:px-2 [&_th]:py-1.5 [&_th]:border [&_th]:border-zinc-700 [&_td]:px-2 [&_td]:py-1.5 [&_td]:border [&_td]:border-zinc-700 [&_tbody_tr:nth-child(odd)]:bg-zinc-900/25">
                                {children}
                            </table>
                        </div>
                    )
                }}
                remarkPlugins={[remarkGfm]}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}

export const AssistantMarkdownMessage = memo(
    ({
        markdown,
        enableDetailsCollapse = true,
        hideRawToggle = false
    }: {
        markdown: string;
        enableDetailsCollapse?: boolean;
        hideRawToggle?: boolean;
    }) => {
        const [showRaw, setShowRaw] = useState(false);

        const split = useMemo(
            () => (enableDetailsCollapse ? splitMarkdownIntoSections(markdown) : null),
            [enableDetailsCollapse, markdown]
        );

        if (showRaw) {
            return (
                <div className="space-y-2">
                    {!hideRawToggle && (
                        <div className="flex justify-end">
                            <button
                                className="rounded px-2 py-0.5 text-[11px] text-zinc-300 hover:text-white hover:bg-zinc-800/60"
                                title="Show rendered output"
                                type="button"
                                onClick={() => setShowRaw(false)}
                            >
                                Rendered
                            </button>
                        </div>
                    )}
                    <pre className="whitespace-pre-wrap break-words rounded border border-zinc-700 bg-zinc-950/40 p-2 text-[12px] text-zinc-200">
                        {normalizeMarkdown(markdown)}
                    </pre>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {!hideRawToggle && (
                    <div className="flex justify-end">
                        <button
                            className="rounded px-2 py-0.5 text-[11px] text-zinc-300 hover:text-white hover:bg-zinc-800/60"
                            title="Show raw Markdown"
                            type="button"
                            onClick={() => setShowRaw(true)}
                        >
                            Raw
                        </button>
                    </div>
                )}

                {split ? (
                    <div className="space-y-2">
                        <MarkdownRenderer markdown={split.head} />
                        <details className="rounded border border-zinc-700 bg-zinc-950/20 px-2 py-1">
                            <summary className="cursor-pointer select-none text-[12px] text-zinc-200 hover:text-white">Details</summary>
                            <div className="mt-2">
                                <MarkdownRenderer markdown={split.details} />
                            </div>
                        </details>
                        <MarkdownRenderer markdown={split.tail} />
                    </div>
                ) : (
                    <MarkdownRenderer markdown={markdown} />
                )}
            </div>
        );
    }
);
